import React from 'react';
import PropTypes from 'prop-types';
import styles from './inlineChoiceInteraction.scss';

import choiceSequenceHandler from './../../../../qti/utils/ChoiceSequenceHandler';
import LoadTracker from '../../../../services/LoadTracker';
import InteractionOutcomeIndicator from '../../interactionOutcome/InteractionOutcomeIndicator';
import ResponsesInfo from './ResponsesInfo';

import navigationService from '../../../../services/NavigationService';
import DefaultSelectInlineChoice from './DefaultSelectInlineChoice';
import responseService from '../../../../services/ResponseService';
import scorableItemNumberingService from '../../../../services/questionView/ScorableItemNumberingService';
import HorizontalSelectList from './HorizontalSelectList';
import sessionReader from '../../../../store/readHelpers/SessionReader';
import storeManager from '../../../../store/StoreManager';
import HiddenItemFlagButton from '../../itemFlag/HiddenItemFlagButton';

class InlineChoiceInteraction extends React.PureComponent {
    static propTypes = {
        choiceSequence: PropTypes.array,
        handleInteractionReady: PropTypes.func.isRequired,
        isReadOnly: PropTypes.bool,
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        userResponse: PropTypes.array.isRequired, // array with simpleChoice identifiers
        correctResponses: PropTypes.array,
        skipUnansweredIndicator: PropTypes.bool,
        questionModel: PropTypes.object,
    };

    static shouldShowOutcomeIndicator(userResponse, correctResponses, skipUnansweredIndicator) {
        const value = responseService.getValue(userResponse);
        const isAttempted = value !== undefined && value !== '';
        return correctResponses !== undefined
            && (isAttempted || !skipUnansweredIndicator);
    }

    static shouldShowAllAlternatives(isReadOnly) {
        return !!isReadOnly;
    }

    static shouldShowAlternativesInfo(isReadOnly, correctResponses) {
        return InlineChoiceInteraction.shouldShowAllAlternatives(isReadOnly) || correctResponses !== undefined;
    }

    constructor(props) {
        const { skipUnansweredIndicator, userResponse, correctResponses, questionModel, model } = props;
        super(props);
        this.mediaTracker = new LoadTracker((success) => {
            this.props.handleInteractionReady(success);
        }, `Interaction ${this.props.model.identifier}`);
        this.onInputReady = this.mediaTracker.addItemToTrack(`Interaction ${this.props.model.identifier} input`);
        if (InlineChoiceInteraction.shouldShowOutcomeIndicator(userResponse, correctResponses, skipUnansweredIndicator)) {
            this.onIconReady = this.mediaTracker.addItemToTrack(`Interaction ${this.props.model.identifier} outcomeIcon`);
        }
        this.mediaTracker.setAllItemsAdded();
        this.interactionNumber = scorableItemNumberingService.getInteractionNumber(questionModel.contentRevisionId, model.identifier);
    }

    componentDidMount() {
        this.onInputReady(true);
    }

    handleAlternativeBoxClick = () => {
        const {
            questionModel: {
                parentId,
                contentRevisionId,
            },
            model: {
                hasGranularNumbering,
            },
        } = this.props;

        if (!hasGranularNumbering) return;

        let sectionId;

        if (sessionReader.isModeQuestionSet()) {
            sectionId = parentId;
        } else {
            const { currentlyShown } = storeManager.getState();
            sectionId = currentlyShown.sectionId;
        }

        navigationService.navigateToScorableItem(sectionId, contentRevisionId, Number(this.interactionNumber));
    }

    focusElement = (isItemPreSelected, element) => {
        if (isItemPreSelected && element) {
            element.focus();
        }
    }


    navigateToScorableItem = () => {
        const { model, questionModel } = this.props;
        scorableItemNumberingService.navigateToScorableItem(questionModel.contentRevisionId, model.identifier, undefined, true);
    };

    render() {
        const {
            choiceSequence,
            skipUnansweredIndicator,
            isReadOnly,
            userResponse,
            correctResponses,
            model,
            questionModel,
        } = this.props;
        const { hasHorizontalRender } = model;
        const value = responseService.getValue(userResponse);
        const isAttempted = value !== undefined && value !== '';
        const alternatives = responseService.getAlternatives(model);

        if (choiceSequence !== undefined) {
            choiceSequenceHandler.sortAlternativesAccordingToChoiceSequence(alternatives, choiceSequence);
        }

        const shouldShowAlternativesInfo = InlineChoiceInteraction.shouldShowAlternativesInfo(isReadOnly, correctResponses);
        const idOnAlternativeWrapper = this.interactionNumber
            ? `scorableItem-${questionModel.contentRevisionId}_${this.interactionNumber}`
            : undefined;
        const addHiddenFocusableFlag = scorableItemNumberingService
            .shouldAddHiddenFlag(questionModel.contentRevisionId, model.identifier, null);

        const classList = ['interaction'];
        if (shouldShowAlternativesInfo) {
            classList.push(styles.checkAnswersMode);
        }

        return (
            <span
                className={classList.join(' ')}
                id={idOnAlternativeWrapper}
                onFocus={this.navigateToScorableItem}
                role="button"
            >
                { hasHorizontalRender || shouldShowAlternativesInfo
                    ? <HorizontalSelectList
                        alternatives={alternatives}
                        value={value}
                        questionProps={this.props}
                        focusElement={this.focusElement}
                        handleAlternativeBoxClick={this.handleAlternativeBoxClick}
                    />
                    : <DefaultSelectInlineChoice
                        alternatives={alternatives}
                        value={value}
                        questionProps={this.props}
                        focusElement={this.focusElement}
                        handleAlternativeBoxClick={this.handleAlternativeBoxClick}
                    />
                }

                { InlineChoiceInteraction.shouldShowOutcomeIndicator(userResponse, correctResponses, skipUnansweredIndicator) &&
                    <InteractionOutcomeIndicator
                        answerOption={value}
                        correctResponses={correctResponses}
                        isAttempted={isAttempted}
                        onReady={this.onIconReady}
                        skipUnansweredIndicator={skipUnansweredIndicator}
                    />
                }

                { shouldShowAlternativesInfo &&
                    <ResponsesInfo
                        showAllAlternatives={InlineChoiceInteraction.shouldShowAllAlternatives(isReadOnly)}
                        value={value}
                        alternatives={alternatives}
                        correctResponses={correctResponses}
                    />
                }
                {
                    addHiddenFocusableFlag && <HiddenItemFlagButton
                        orderNumber={this.interactionNumber}
                        responseIdentifier={model.identifier}
                        contentRevisionId={questionModel.contentRevisionId}
                    />
                }
            </span>
        );
    }
}

export default InlineChoiceInteraction;
